import React, { Provider } from 'react'
import { renderToString } from 'react-dom/server'
import { Inline } from '@contentful/rich-text-types'
import { TypeComponentLink } from '@lib/types'
import { ComponentContentTypes } from '@lib/types/content-types'
import { ImageAsset } from '@components/asset/image'
import { CtaButton } from '@components/item/cta-button'
import { NinetailedMergeTag } from '@lib/ninetailed/merge-tag'
import { ComponentLink } from '../../item/link/component-link'
import { getShortcodeName, Shortcode } from './shortcode'

type InlineEmbeddedEntryProps = Inline

const getItemGlobalVariable = (inline: Inline): string | null => {
  return inline.data?.target?.fields?.value ?? null
}

export const InlineEmbeddedEntry: React.FC<InlineEmbeddedEntryProps> = (props: InlineEmbeddedEntryProps) => {
  switch (props.data?.target?.sys?.contentType?.sys?.id) {
    case ComponentContentTypes.ItemGlobalVariable:
      return <>{getItemGlobalVariable(props)}</>
    case ComponentContentTypes.ItemShortcode:
      return <Shortcode codeName={getShortcodeName(props)} />
    case ComponentContentTypes.ComponentLink:
      return <ComponentLink isEmbedded targetLink={props.data.target as TypeComponentLink} />
    case ComponentContentTypes.NinetailedMergeTag:
      return (
        <NinetailedMergeTag
          id={props.data.target.fields.nt_mergetag_id}
          fallback={props.data.target.fields.nt_fallback}
        />
      )
    case ComponentContentTypes.AssetImage:
      return <ImageAsset structure={props.data.target} />
    case ComponentContentTypes.ComponentCtaButton:
      return <CtaButton structure={props.data.target} />
  }

  return null
}

export const inlineEmbeddedEntryHtmlString = <C extends object | undefined>(
  props: Inline,
  ContextProvider?: Provider<C>,
  contextValue?: C
): string => {
  switch (props.data?.target?.sys?.contentType?.sys?.id) {
    case ComponentContentTypes.ItemGlobalVariable:
      return getItemGlobalVariable(props) ?? ''
    case ComponentContentTypes.ItemShortcode:
      return renderToString(
        ContextProvider && contextValue ? (
          <ContextProvider value={contextValue}>
            <Shortcode codeName={getShortcodeName(props)} />
          </ContextProvider>
        ) : (
          <Shortcode codeName={getShortcodeName(props)} />
        )
      )
    case ComponentContentTypes.ComponentLink:
      return renderToString(<ComponentLink targetLink={props.data.target as TypeComponentLink} />)
  }

  return ''
}
